import { useMemo } from 'react'
import Select, { GroupBase, Props, PropsValue } from 'react-select'
import { isLooseOptionSelected, resolveSelectValue, SelectOption } from './reactSelectHelpers'

type LooseSingleValue<Option extends SelectOption> =
  | PropsValue<Option>
  | string
  | number

type AppSelectProps<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<Props<Option, IsMulti, Group>, 'value'> & {
  value?: IsMulti extends true ? Props<Option, IsMulti, Group>['value'] : LooseSingleValue<Option>
}

/**
 * Drop-in react-select wrapper with loose value matching (string vs number ids).
 * Use anywhere filter/list lookups build value objects separately from options.
 */
function AppSelect<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AppSelectProps<Option, IsMulti, Group>) {
  const { value, options, classNamePrefix = 'react-select', isOptionSelected, isMulti, ...rest } =
    props

  const resolvedValue = useMemo(() => {
    if (isMulti) return value as PropsValue<Option>
    return resolveSelectValue(
      value as Option | string | number | null | undefined,
      options as readonly Option[] | undefined
    )
  }, [value, options, isMulti])

  return (
    <Select
      classNamePrefix={classNamePrefix}
      isOptionSelected={isOptionSelected ?? isLooseOptionSelected}
      isMulti={isMulti}
      {...rest}
      options={options}
      value={resolvedValue}
    />
  )
}

export default AppSelect
